// Portado de aw-ava/app/src/componentes/ui/basicos.tsx (kit aprovado pelo PO). Mudou só o caminho dos imports.
// Componentes pequenos e sem estado: Card, Badge, Progress, Input, Label,
// Textarea, Avatar, Skeleton, Separator, Switch, Checkbox.

import * as AvatarPrimitive from '@radix-ui/react-avatar'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import * as LabelPrimitive from '@radix-ui/react-label'
import * as ProgressPrimitive from '@radix-ui/react-progress'
import * as SeparatorPrimitive from '@radix-ui/react-separator'
import * as SwitchPrimitive from '@radix-ui/react-switch'
import { cva, type VariantProps } from 'class-variance-authority'
import { Check } from 'lucide-react'
import * as React from 'react'
import { cn, iniciais } from '../cn'

export const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...p }, ref) => (
    <div
      ref={ref}
      className={cn(
        'rounded-[var(--radius-lg)] border border-(--cartao-borda) bg-surface shadow-[var(--shadow-card)]',
        className,
      )}
      {...p}
    />
  ),
)
Card.displayName = 'Card'

const badgeVariantes = cva(
  'inline-flex min-h-(--badge-h) items-center gap-1 rounded-(--badge-raio) px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap',
  {
    variants: {
      variante: {
        neutro: 'bg-(--badge-neutro) text-text-2',
        escuro: 'bg-text text-surface',
        primario: 'bg-primary-soft text-primary-texto',
        ok: 'bg-ok-soft text-ok',
        aviso: 'bg-aviso-soft text-aviso',
        erro: 'bg-erro-soft text-erro',
        contorno: 'border border-line text-text-2',
      },
    },
    defaultVariants: { variante: 'neutro' },
  },
)
/** `ponto`: o ponto de 6px na cor do texto antes da palavra (linha de base 2: estado nunca só por cor). */
export function Badge({
  className,
  variante,
  ponto,
  children,
  ...p
}: React.HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariantes> & { ponto?: boolean }) {
  return (
    <span className={cn(badgeVariantes({ variante }), ponto && 'gap-1.5', className)} {...p}>
      {ponto && <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current" />}
      {children}
    </span>
  )
}

export const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> & { valor: number; altura?: number; cor?: string }
>(({ className, valor, altura = 6, cor, ...p }, ref) => (
  <ProgressPrimitive.Root
    ref={ref}
    className={cn('relative w-full overflow-hidden rounded-full bg-line', className)}
    style={{ height: altura }}
    value={valor}
    {...p}
  >
    <ProgressPrimitive.Indicator
      className={cn('h-full rounded-full transition-[width] duration-500', cor ?? 'bg-primary')}
      style={{ width: `${Math.min(100, Math.max(0, valor))}%` }}
    />
  </ProgressPrimitive.Root>
))
Progress.displayName = 'Progress'

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, type, onFocus, ...p }, ref) => (
    <input
      ref={ref}
      type={type}
      // campo numérico: ao entrar (clique ou Tab), o valor fica selecionado e o que se digita o substitui
      onFocus={(e) => {
        if (type === 'number') e.currentTarget.select()
        onFocus?.(e)
      }}
      className={cn(
        'flex h-(--ctl-h) w-full rounded-[var(--radius-md)] border border-(--campo-borda) bg-surface px-3.5 text-(length:--ctl-texto) text-text placeholder:text-text-3 transition-shadow focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--primary-ring)] focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 file:border-0 file:bg-transparent file:text-sm file:font-medium',
        className,
      )}
      {...p}
    />
  ),
)
Input.displayName = 'Input'

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className, ...p }, ref) => (
    <textarea
      ref={ref}
      className={cn(
        'flex min-h-[90px] w-full rounded-[var(--radius-md)] border border-(--campo-borda) bg-surface px-3.5 py-2.5 text-(length:--ctl-texto) text-text placeholder:text-text-3 transition-shadow focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--primary-ring)] focus-visible:border-primary disabled:opacity-50 resize-y',
        className,
      )}
      {...p}
    />
  ),
)
Textarea.displayName = 'Textarea'

export const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...p }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn(
      'text-(length:--rotulo-texto) font-semibold text-text leading-none peer-disabled:opacity-70',
      className,
    )}
    {...p}
  />
))
Label.displayName = 'Label'

export function Campo({
  rotulo,
  dica,
  erro,
  children,
  className,
  id,
}: {
  rotulo?: string
  dica?: React.ReactNode
  erro?: string
  children: React.ReactNode
  className?: string
  id?: string
}) {
  return (
    <div className={cn('grid grid-cols-[minmax(0,1fr)] content-start gap-1.5', className)}>
      {rotulo && <Label htmlFor={id}>{rotulo}</Label>}
      {children}
      {erro ? (
        <p id={id ? `${id}-erro` : undefined} role="alert" className="text-xs text-erro">
          {erro}
        </p>
      ) : dica ? (
        <p className="text-xs text-text-3">{dica}</p>
      ) : null}
    </div>
  )
}

export function Avatar({
  nome,
  url,
  tamanho = 34,
  className,
}: {
  nome: string
  url?: string | null
  tamanho?: number
  className?: string
}) {
  return (
    <AvatarPrimitive.Root
      className={cn(
        'relative inline-flex shrink-0 overflow-hidden rounded-full bg-(--avatar-bg) text-(--avatar-texto) font-bold select-none',
        className,
      )}
      style={{ width: tamanho, height: tamanho, fontSize: tamanho * 0.38 }}
    >
      {url && <AvatarPrimitive.Image src={url} alt={nome} className="size-full object-cover" />}
      <AvatarPrimitive.Fallback className="grid size-full place-items-center" delayMs={url ? 300 : 0}>
        {iniciais(nome)}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  )
}

export function Skeleton({ className, ...p }: React.HTMLAttributes<HTMLSpanElement>) {
  // span (não div) para poder viver dentro de <p> sem aviso do React.
  return <span className={cn('esqueleto block', className)} {...p} />
}

export const Separator = React.forwardRef<
  React.ElementRef<typeof SeparatorPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SeparatorPrimitive.Root>
>(({ className, orientation = 'horizontal', decorative = true, ...p }, ref) => (
  <SeparatorPrimitive.Root
    ref={ref}
    decorative={decorative}
    orientation={orientation}
    className={cn('shrink-0 bg-line', orientation === 'horizontal' ? 'h-px w-full' : 'h-full w-px', className)}
    {...p}
  />
))
Separator.displayName = 'Separator'

export const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>
>(({ className, ...p }, ref) => (
  <SwitchPrimitive.Root
    ref={ref}
    className={cn(
      'peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors data-[state=checked]:bg-[var(--switch-on)] data-[state=unchecked]:bg-[var(--switch-off)] disabled:opacity-50',
      className,
    )}
    {...p}
  >
    <SwitchPrimitive.Thumb className="pointer-events-none block size-5 rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=checked]:bg-[var(--switch-bola-on)] data-[state=unchecked]:translate-x-0" />
  </SwitchPrimitive.Root>
))
Switch.displayName = 'Switch'

export const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...p }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      'peer size-5 shrink-0 rounded-[5px] border border-line-forte bg-surface data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-on-primary disabled:opacity-50',
      className,
    )}
    {...p}
  >
    <CheckboxPrimitive.Indicator className="grid place-items-center text-current">
      <Check className="size-3.5" strokeWidth={3} />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
))
Checkbox.displayName = 'Checkbox'

export function Vazio({
  icone,
  titulo,
  texto,
  acao,
}: {
  icone?: React.ReactNode
  titulo: string
  texto?: string
  acao?: React.ReactNode
}) {
  return (
    <div className="grid place-items-center gap-2 rounded-[var(--radius-lg)] border border-dashed border-(--vazio-borda) bg-(--vazio-bg) px-6 py-12 text-center">
      {icone && (
        <div className="grid size-12 place-items-center rounded-full bg-(--vazio-icone-bg) text-text-3 shadow-(--vazio-icone-sombra) [&_svg]:size-6">
          {icone}
        </div>
      )}
      <h3 className="text-base font-semibold">{titulo}</h3>
      {texto && <p className="max-w-sm text-sm text-text-2">{texto}</p>}
      {acao && <div className="mt-2">{acao}</div>}
    </div>
  )
}
